import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPostJson } from './client'

/**
 * Settings → Easeedesk → Email notifications si Mobile notifications.
 *
 * Perechile vechi: `settings/easeedesk/notifications_settings`,
 * `notifications_list/{tip}`, `eventNotifications`, `add_notification_form`,
 * `edit_notification_form`, `create_notification`, `update_notification`,
 * `remove_notification`, si cele patru de telefon
 * (`mobile_notifications_settings`, `mobile_notifications_list/{app}/{tip}`,
 * `mobileEventNotifications`, `add_mobile_notification_form`).
 *
 * CE SCRIE ECRANUL ASTA E CE CITESTE COADA DE INSTIINTARI. Un rand de aici -
 * `it_easeedesk_notifications_scheme` - spune, pentru un tip de tichet si un
 * eveniment: cine primeste (`user_type`), in ce limba, cu ce sablon, de la ce
 * nume si ce adresa, cu semnatura sau fara, sub ce conditii si pentru ce firme.
 * Cele noua campuri sunt exact cele pe care le cerne
 * `application/Services/Easeedesk/Notifications/Plan.php`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface NotificationTicketType {
  ID: number
  type_name: string
}

export interface NotificationsShell {
  title: string
  moduleUrl: string
  ticketTypes: NotificationTicketType[]
  /**
   * `appsList` din ecranul de telefon: azi doar StarTeam (6). Ecranul de email
   * n-are casuta asta, deci campul lipseste de acolo - un singur fel de raspuns
   * pentru amandoua, fiindca pagina e aceeasi.
   */
  apps?: { ID: number; name: string }[]
}

export interface NotificationEvent {
  ID: number
  event: string
  description: string
  /** Cate reguli are evenimentul pe tipul de tichet ales. */
  notifications: number
}

export interface NotificationEvents {
  ticket_type: number
  application_type: number
  events: NotificationEvent[]
}

export interface NotificationTemplate {
  ID: number
  template_name: string
}

export interface NotificationUserType {
  /** `it_easeedesk_user_type.ID` - vezi `UserTypes` din application/Services. */
  user_type_id: number
  display_name: string
}

export interface NotificationStatus {
  ID: number
  status_name: string
}

export interface NotificationCompany {
  ID: number
  name: string
}

/** Listele din care alege omul; aceleasi pe toate machetele grupului. */
export interface NotificationOptions {
  templates: NotificationTemplate[]
  userTypes: NotificationUserType[]
  /** Numele conditiilor pe care le stie evenimentul (`ticket_status`, `escalation_type`). */
  eventConditions: string[]
  statuses: NotificationStatus[]
  companies: NotificationCompany[]
  languages: string[]
}

export interface NotificationRule {
  ID: number
  user_type: number
  ticket_type: number
  event_id: number
  language: string
  template_id: number
  /** 1 = numele din setarile serverului, 2 = numele operatorului. */
  from_name: number
  from_email: number
  add_signature: number
  template_name: string
  display_name: string
  conditions: Record<string, string>
  related_companies: number[]
}

export interface NotificationEventDetail extends NotificationOptions {
  ticket_type: number
  event_id: number
  notifications: NotificationRule[]
}

export interface NotificationForm extends NotificationOptions {
  ticket_type: number
  event_id: number
}

/** Ce se trimite la salvare. `cached` din raspuns spune daca Redis a fost refacut. */
export interface NotificationInput {
  ticket_type?: number
  event_id?: number
  user_type: number
  language: string
  template_id: number
  from_name: number
  from_email: number
  add_signature: number
  conditions?: Record<string, string>
  companies?: number[]
}

export interface NotificationSaved {
  ok?: boolean
  ID?: number
  /** `false` = randul e salvat, dar coada citeste inca setarile vechi. */
  cached?: boolean
}

/* ------------------------------------------------------------------- EMAIL */

export function notificationsShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'notifications'],
    queryFn: () => apiGet<NotificationsShell>('/settings/easeedesk/notifications'),
  })
}

export function notificationEventsQuery(ticketType: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'notifications', 'list', ticketType],
    queryFn: () => apiGet<NotificationEvents>(`/settings/easeedesk/notifications/list?ticket_type=${ticketType}`),
    enabled: ticketType > 0,
  })
}

export function notificationEventQuery(ticketType: number, event: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'notifications', 'event', ticketType, event],
    queryFn: () =>
      apiGet<NotificationEventDetail>(
        `/settings/easeedesk/notifications/event?ticket_type=${ticketType}&event=${event}`,
      ),
    enabled: ticketType > 0 && event > 0,
  })
}

export function notificationFormQuery(ticketType: number, event: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'notifications', 'form', ticketType, event],
    queryFn: () =>
      apiGet<NotificationForm>(`/settings/easeedesk/notifications/form?ticket_type=${ticketType}&event=${event}`),
    enabled: ticketType > 0 && event > 0,
  })
}

export function createNotification(input: NotificationInput) {
  return apiPostJson<NotificationSaved>('/settings/easeedesk/notifications', input)
}

export function updateNotification(id: number, input: NotificationInput) {
  return apiPostJson<NotificationSaved>(`/settings/easeedesk/notifications/${id}`, input)
}

export function removeNotification(id: number) {
  return apiPostJson<NotificationSaved>(`/settings/easeedesk/notifications/${id}/remove`, {})
}

/* ------------------------------------------------------------------ MOBILE */

export function mobileNotificationsShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'mobile_notifications'],
    queryFn: () => apiGet<NotificationsShell>('/settings/easeedesk/mobile_notifications'),
  })
}

export function mobileNotificationEventsQuery(applicationType: number, ticketType: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'mobile_notifications', 'list', applicationType, ticketType],
    queryFn: () =>
      apiGet<NotificationEvents>(
        `/settings/easeedesk/mobile_notifications/list?application_type=${applicationType}&ticket_type=${ticketType}`,
      ),
    enabled: applicationType > 0 && ticketType > 0,
  })
}

export function mobileNotificationEventQuery(ticketType: number, event: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'mobile_notifications', 'event', ticketType, event],
    queryFn: () =>
      apiGet<NotificationEventDetail>(
        `/settings/easeedesk/mobile_notifications/event?ticket_type=${ticketType}&event=${event}`,
      ),
    enabled: ticketType > 0 && event > 0,
  })
}

export function mobileNotificationFormQuery(ticketType: number, event: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'mobile_notifications', 'form', ticketType, event],
    queryFn: () =>
      apiGet<NotificationForm>(
        `/settings/easeedesk/mobile_notifications/form?ticket_type=${ticketType}&event=${event}`,
      ),
    enabled: ticketType > 0 && event > 0,
  })
}

export function createMobileNotification(input: NotificationInput) {
  return apiPostJson<NotificationSaved>('/settings/easeedesk/mobile_notifications', input)
}

export function updateMobileNotification(id: number, input: NotificationInput) {
  return apiPostJson<NotificationSaved>(`/settings/easeedesk/mobile_notifications/${id}`, input)
}

export function removeMobileNotification(id: number) {
  return apiPostJson<NotificationSaved>(`/settings/easeedesk/mobile_notifications/${id}/remove`, {})
}
